import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import { ReactComponent as InfoIcon } from '../../../../../assets/svg/info-icon.svg';
import { useStore } from '../../../../../store';
import { LinkedAccount, useLinkedAccounts } from '../../../hooks/useLinkedAccounts';
import { TransactionTabs } from '../../../state/createTransactionSlice';
import SyncCardModal from '../SyncCardModal';
import { UnlinkCardModal } from '../UnlinkCardModal';
import { AddNewCardButton } from './AddNewCardButton';
import { ManageCardsItem } from './ManageCardsItem';

interface ManageCardsProps {
  syncedCards: LinkedAccount[];
}

const ManageCards = (props: ManageCardsProps) => {
  const { syncedCards } = props;
  const { t } = useTranslation();
  const [showUnlinkModal, setShowUnlinkModal] = useState<boolean>(false);
  const [selectedCard, setSelectedCard] = useState<LinkedAccount>(syncedCards[0]);
  const [showPlaidOnly, setShowPlaidOnly] = useState<boolean>(false);
  const { refetch } = useLinkedAccounts();
  const isAddCardDisabled = useMemo(() => syncedCards.length >= 3, [syncedCards]);
  const { setSelectedTransactionTab } = useStore((state) => state);
  const setShowSyncModal = useStore((state) => state.setShowSyncCardModal);
  const setSelectedSyncedCard = useStore((state) => state.setSelectedSyncedCard);
  const [expiredCard, setExpiredCard] = useState<LinkedAccount | null>(null);

  const handleUnlink = (card: LinkedAccount) => {
    setSelectedCard(card);
    setShowUnlinkModal(true);
  };

  const handleUnlinkModalClose = async () => {
    const { data: newlyLinkedAccounts } = await refetch();
    setShowPlaidOnly(false);
    setShowUnlinkModal(false);
    if (newlyLinkedAccounts && newlyLinkedAccounts.length === 0) {
      setSelectedTransactionTab(TransactionTabs.BANK_TRANSACTIONS);
    } else if (
      newlyLinkedAccounts
      && newlyLinkedAccounts.length > 0
      && !newlyLinkedAccounts.some((card: LinkedAccount) => card.externalId === selectedCard.externalId)
    ) {
      setSelectedSyncedCard(newlyLinkedAccounts[0]);
    }
  };

  const handleAddNewCard = () => {
    setShowPlaidOnly(false);
    setShowSyncModal(true);
  };

  const handleRelinkExpiredCard = (card: LinkedAccount) => {
    setExpiredCard(card);
    setShowPlaidOnly(true);
  };

  return (
    <div className='flex flex-col h-full justify-between'>
      <div className='font-metricR text-[16px] text-bw07 mt-[16px] ml-[24px]'>
        {t('TRANSACTIONS_FACELIFT.SYNC_CARD.SYNCED_CARDS')}
      </div>
      <div className='flex flex-col h-full'>
        {syncedCards.map((card) => (
          <div key={card.externalId} id={card.externalId}>
            <ManageCardsItem
              linkedCard={card}
              handleUnlink={handleUnlink}
              handleRelinkExpiredCard={handleRelinkExpiredCard}
              selectedCard={selectedCard}
            />
          </div>
        ))}
        <AddNewCardButton isAddCardDisabled={isAddCardDisabled} handleAddNewCard={handleAddNewCard} />
        <Tooltip id='unlink-tooltip' />
      </div>
      <div className='flex flex-row gap-[4px] mb-[30px] ml-[24px] items-center'>
        <InfoIcon />
        <div className='font-metricR text-[13px] text-bw08'>{t('TRANSACTIONS_FACELIFT.SYNC_CARD.SYNC_NOTICE')}</div>
      </div>
      <SyncCardModal showPlaidOnly={showPlaidOnly} expiredCard={expiredCard} />
      {showUnlinkModal && selectedCard && (
        <UnlinkCardModal
          onFinished={handleUnlinkModalClose}
          card={selectedCard}
          onCanceled={() => setShowUnlinkModal(false)}
        />
      )}
    </div>
  );
};
export default ManageCards;
